/**
 * Cambio obligatorio de clave.
 *
 * Aparece cuando la clave es provisoria: la puso un administrador, en un alta
 * o en un restablecimiento, y por lo tanto la conoce alguien más. Hasta que la
 * persona la cambie no se muestra la consola, así que no hay forma de operar
 * con una clave que otro sabe.
 *
 * No tiene "Cancelar" a propósito. La única salida es cambiarla o cerrar la
 * sesión.
 */

import { useState } from 'react';
import { KeyRound, Loader2, LogOut } from 'lucide-react';

import { mensajeDe } from '../../api';
import { Aviso } from '../../components/Aviso';
import { useSesion } from '../../components/useSesion';
import { cambiarClavePropia } from './api';

/** Si el back no lo informa, se usa el mismo valor que él trae por defecto. */
const LARGO_MINIMO_POR_DEFECTO = 6;

export function CambioClave({
  usuario,
  onListo,
  onSalir,
}: {
  readonly usuario: string;
  readonly onListo: () => void;
  readonly onSalir: () => void;
}): React.JSX.Element {
  const [claveActual, setClaveActual] = useState('');
  const [claveNueva, setClaveNueva] = useState('');
  const [repetida, setRepetida] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);

  // El mínimo lo define el back: acá solo se avisa antes de mandar el pedido.
  const { modulo } = useSesion();
  const largoMinimo = modulo?.largoMinimoClave ?? LARGO_MINIMO_POR_DEFECTO;

  const corta = claveNueva.length > 0 && claveNueva.length < largoMinimo;
  const noCoinciden = repetida.length > 0 && repetida !== claveNueva;

  const enviar = async (): Promise<void> => {
    setGuardando(true);
    try {
      await cambiarClavePropia(claveActual, claveNueva);
      setError(null);
      onListo();
    } catch (err) {
      setError(mensajeDe(err));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <section className="ingreso">
      <img src="/simbolo-azul.png" alt="CodyLog" />
      <p className="rubro">Recursos Humanos</p>
      <h2>Cambiá tu clave</h2>
      <p className="nota">
        {`Hola ${usuario}. Tu clave es provisoria: la generó un Administrador, así que la conoce alguien más. Elegí una nueva para poder seguir.`}
      </p>

      {error !== null && <Aviso tono="error">{error}</Aviso>}

      <form
        className="formulario"
        onSubmit={(ev) => {
          ev.preventDefault();
          void enviar();
        }}
      >
        <label className="campo">
          Clave actual
          <input
            type="password"
            required
            autoComplete="current-password"
            value={claveActual}
            onChange={(ev) => setClaveActual(ev.target.value)}
          />
        </label>
        <label className="campo">
          Clave nueva
          <input
            type="password"
            required
            autoComplete="new-password"
            minLength={largoMinimo}
            value={claveNueva}
            onChange={(ev) => setClaveNueva(ev.target.value)}
          />
        </label>
        <label className="campo">
          Repetir la clave nueva
          <input
            type="password"
            required
            autoComplete="new-password"
            value={repetida}
            onChange={(ev) => setRepetida(ev.target.value)}
          />
        </label>

        <p className="nota">
          {corta
            ? `Faltan caracteres: el mínimo es ${String(largoMinimo)}.`
            : noCoinciden
              ? 'Las dos claves nuevas no coinciden.'
              : `Mínimo ${String(largoMinimo)} caracteres.`}
        </p>

        <div className="acciones">
          <button type="submit" disabled={guardando || corta || noCoinciden}>
            {guardando ? (
              <Loader2 className="girando" size={16} aria-hidden="true" />
            ) : (
              <KeyRound size={16} aria-hidden="true" />
            )}
            {guardando ? 'Guardando…' : 'Cambiar la clave'}
          </button>
          <button type="button" className="plano" onClick={onSalir}>
            <LogOut size={15} aria-hidden="true" />
            Salir
          </button>
        </div>
      </form>
    </section>
  );
}
